import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';

export type IncomePaymentStage = 'BOOKING' | 'DEPOSIT' | 'BALANCE' | 'FULL_PAYMENT';
export type IncomePaymentMethod = 'CASH' | 'BANK_TRANSFER' | 'CREDIT_CARD';
export type FeeResponsibility = 'CUSTOMER' | 'COMPANY' | null;

export interface IncomeItem {
  id: string;
  paymentNo: string;
  paymentDate: string;
  bookingNo: string;
  tourCode: string;
  tourName: string;
  customerName: string;
  payerName: string;
  paymentStage: IncomePaymentStage;
  paymentMethod: IncomePaymentMethod;
  amount: number;
  customerPaidAmount: number;
  withholdingTaxAmount: number;
  creditCardFeePercent: number;
  creditCardFeeAmount: number;
  creditCardFeeResponsibility: FeeResponsibility;
  verifiedByName: string;
  verifiedAt: string | null;
  slipUrl: string | null;
  receiptUrl: string;
  quotationUrl: string;
}

export interface IncomeFilters {
  dateFrom: string;
  dateTo: string;
  paymentStage: 'ALL' | IncomePaymentStage;
  paymentMethod: 'ALL' | IncomePaymentMethod;
}

export interface UpdateIncomeDocumentsInput {
  receiptUrl: string;
  quotationUrl: string;
}

@Injectable({ providedIn: 'root' })
export class IncomeService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/accounting/income`;

  async getItems(filters: IncomeFilters): Promise<IncomeItem[]> {
    let params = new HttpParams()
      .set('paymentStage', filters.paymentStage)
      .set('paymentMethod', filters.paymentMethod);
    if (filters.dateFrom) params = params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params = params.set('dateTo', filters.dateTo);

    const response = await firstValueFrom(
      this.http.get<{ items: IncomeItem[] }>(this.apiUrl, {
        params,
        withCredentials: true,
      }),
    );
    return response.items;
  }

  async updateDocuments(id: string, input: UpdateIncomeDocumentsInput): Promise<void> {
    await firstValueFrom(
      this.http.patch(`${this.apiUrl}/${id}/documents`, input, {
        withCredentials: true,
      }),
    );
  }

  resolveFileUrl(fileUrl: string): string {
    if (/^https?:\/\//i.test(fileUrl)) return fileUrl;
    if (/^https?:\/\//i.test(environment.apiUrl)) {
      return `${new URL(environment.apiUrl).origin}${fileUrl}`;
    }
    return fileUrl;
  }
}
